import { useEffect, useState, useMemo, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { 
  Activity, 
  Users, 
  Clock, 
  MousePointerClick, 
  TrendingUp,
  TrendingDown,
  Globe,
  Smartphone,
  Monitor,
  Tablet,
  FileText,
  UserPlus,
  DollarSign,
  BarChart3,
  PieChart as PieChartIcon,
  Zap,
  Target,
  Calendar,
  CheckCircle2,
  Circle,
  Timer,
  Plus,
  Trash2,
  AlertCircle,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  ListTodo,
  Hand,
  Layers,
  Wallet,
  BanknoteIcon,
  Receipt,
  ThumbsUp,
  ThumbsDown,
  Meh,
  Star,
  UserX,
  ShoppingCart,
  XCircle,
  RefreshCw,
  Phone,
  Send,
  CreditCard
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell,
  Area,
  AreaChart,
  Tooltip,
  LineChart,
  Line,
  Legend,
  ComposedChart
} from 'recharts';
import { GeographyMap } from '@/components/analytics/GeographyMap';
import { format, subDays, startOfDay, endOfDay, eachDayOfInterval, differenceInDays } from 'date-fns';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';

interface SessionData {
  id: string;
  session_id: string;
  user_id: string | null;
  page_route: string;
  time_spent_seconds: number;
  interactions: any;
  cart_data: any;
  abandoned_cart: boolean;
  created_at: string;
  country_code: string | null;
  country_name: string | null;
  city: string | null;
  latitude: number | null;
  longitude: number | null;
  device_type: string | null;
  browser: string | null;
  os: string | null;
}

interface LocationData {
  latitude: number;
  longitude: number;
  city: string;
  country: string;
  count: number;
}

interface EmployeeTask {
  id: string;
  title: string;
  description: string | null;
  assigned_to: string | null;
  status: string;
  priority: string;
  due_date: string | null;
  completed_at: string | null;
  created_at: string;
  inquiry_id: string | null;
}

interface FinancialMetrics {
  pipelineValue: number;
  contractedValue: number;
  signedValue: number;
  collectionsReceived: number;
  collectionsPending: number;
  totalExpenses: number;
  grossProfit: number;
  profitMargin: number;
}

interface NPSData {
  score: number;
  promoters: number;
  passives: number;
  detractors: number;
  totalResponses: number;
}

interface SalesTimingMetrics {
  avgTimeToScheduleCall: number | null; // in hours
  avgTimeToSendContract: number | null; // in hours
  avgTimeToPayment: number | null; // in days
  callsScheduled: number;
  contractsSent: number;
  paymentsReceived: number;
}

const CHART_COLORS = [
  'hsl(160, 84%, 39%)',
  'hsl(200, 80%, 50%)',
  'hsl(280, 70%, 55%)',
  'hsl(45, 90%, 55%)',
  'hsl(0, 70%, 55%)',
  'hsl(180, 70%, 45%)',
];

const formatCurrency = (value: number) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${value.toLocaleString()}`;
};

const formatDuration = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins >= 60) {
    const hours = Math.floor(mins / 60);
    const remainingMins = mins % 60;
    return `${hours}h ${remainingMins}m`;
  }
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};

const formatTimingHours = (hours: number | null) => {
  if (hours === null) return '—';
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${Math.round(hours)}h`;
  const days = Math.round(hours / 24);
  return `${days}d`;
};

const formatTimingDays = (days: number | null) => {
  if (days === null) return '—';
  if (days < 1) return `${Math.round(days * 24)}h`;
  return `${Math.round(days)}d`;
};

const formatButtonName = (name: string) => {
  return name
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .slice(0, 25);
};

const formatRouteName = (route: string) => {
  if (route === '/') return 'Home';
  return route
    .replace(/\//g, '')
    .replace(/-/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .slice(0, 20);
};

const CURRENT_VERSION_DATE = '2025-01-20';

export function ExecutiveDashboard() {
  const [sessions, setSessions] = useState<SessionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30');
  const [financials, setFinancials] = useState<FinancialMetrics>({
    pipelineValue: 0,
    contractedValue: 0,
    signedValue: 0,
    collectionsReceived: 0,
    collectionsPending: 0,
    totalExpenses: 0,
    grossProfit: 0,
    profitMargin: 0,
  });
  const [npsData, setNpsData] = useState<NPSData>({
    score: 0,
    promoters: 0,
    passives: 0,
    detractors: 0,
    totalResponses: 0,
  });
  const [totalInquiries, setTotalInquiries] = useState(0);
  const [pendingInquiries, setPendingInquiries] = useState(0);
  const [confirmedInquiries, setConfirmedInquiries] = useState(0);
  const [totalSignups, setTotalSignups] = useState(0);
  const [previousSignups, setPreviousSignups] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [tasks, setTasks] = useState<EmployeeTask[]>([]);
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', description: '', priority: 'medium', due_date: '' });
  const [churnRate, setChurnRate] = useState(0);
  const [conversionRate, setConversionRate] = useState(0);
  const [salesTiming, setSalesTiming] = useState<SalesTimingMetrics>({
    avgTimeToScheduleCall: null,
    avgTimeToSendContract: null,
    avgTimeToPayment: null,
    callsScheduled: 0,
    contractsSent: 0,
    paymentsReceived: 0,
  });
  const { toast } = useToast();

  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      const startDate = subDays(new Date(), parseInt(timeRange));
      const previousStartDate = subDays(startDate, parseInt(timeRange));
      const versionDate = new Date(CURRENT_VERSION_DATE);
      const effectiveStartDate = startDate > versionDate ? startDate : versionDate;
      
      // Parallel data fetching for performance
      const [
        sessionsResult,
        inquiriesResult,
        previousInquiriesResult,
        profilesResult,
        previousProfilesResult,
        contractsResult,
        paymentsResult,
        expensesResult,
        feedbackResult,
        errorsResult,
        tasksResult
      ] = await Promise.all([
        // Sessions
        supabase
          .from('session_analytics')
          .select('*')
          .gte('created_at', effectiveStartDate.toISOString())
          .order('created_at', { ascending: false }),
        
        // Current period inquiries with call scheduling data
        supabase
          .from('contact_submissions')
          .select('id, status, total_cost, created_at, scheduled_call_at')
          .gte('created_at', startDate.toISOString()),
        
        // Previous period inquiries for comparison
        supabase
          .from('contact_submissions')
          .select('id, status')
          .gte('created_at', previousStartDate.toISOString())
          .lt('created_at', startDate.toISOString()),
        
        // Current signups
        supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', startDate.toISOString()),
        
        // Previous period signups
        supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', previousStartDate.toISOString())
          .lt('created_at', startDate.toISOString()),
        
        // Contracts with sent_at for timing metrics
        supabase
          .from('inquiry_contracts')
          .select('id, inquiry_id, total_amount, deposit_amount, status, signed_at, sent_at, created_at'),
        
        // Payments with contract_id for timing calculations
        supabase
          .from('payment_transactions')
          .select('amount, status, payment_type, received_date, contract_id, created_at'),
        
        // Expenses (COGS)
        supabase
          .from('business_expenses')
          .select('amount, category, expense_date'),
        
        // Customer feedback / NPS
        supabase
          .from('customer_feedback')
          .select('nps_score, created_at'),
        
        // Error logs
        supabase
          .from('error_logs')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', startDate.toISOString()),
        
        // Tasks
        supabase
          .from('employee_tasks')
          .select('*')
          .order('created_at', { ascending: false })
      ]);

      // Process sessions
      if (sessionsResult.data) setSessions(sessionsResult.data);

      // Process inquiries
      if (inquiriesResult.data) {
        setTotalInquiries(inquiriesResult.data.length);
        const pending = inquiriesResult.data.filter(i => i.status === 'pending').length;
        const confirmed = inquiriesResult.data.filter(i => i.status === 'confirmed').length;
        setPendingInquiries(pending);
        setConfirmedInquiries(confirmed);
        
        // Pipeline value from pending inquiries
        const pipelineValue = inquiriesResult.data
          .filter(i => i.status === 'pending')
          .reduce((sum, i) => sum + (i.total_cost || 0), 0);
        
        // Calculate churn - lost inquiries / total
        const previousCount = previousInquiriesResult.data?.length || 1;
        const currentPending = pending;
        const lostRate = previousCount > 0 ? 
          Math.max(0, (previousCount - currentPending) / previousCount * 100) : 0;
        setChurnRate(Math.round(lostRate));
        
        // Conversion rate
        const totalPipeline = inquiriesResult.data.length;
        const convertedCount = contractsResult.data?.filter(c => c.status === 'signed').length || 0;
        const conversion = totalPipeline > 0 ? (convertedCount / totalPipeline * 100) : 0;
        setConversionRate(Math.round(conversion));

        // =========== SALES TIMING METRICS ===========
        
        // 1. Time to schedule call (inquiry created -> scheduled_call_at)
        const inquiriesWithScheduledCalls = inquiriesResult.data.filter(
          i => i.scheduled_call_at && i.created_at
        );
        let avgTimeToScheduleCall: number | null = null;
        if (inquiriesWithScheduledCalls.length > 0) {
          const totalHoursToSchedule = inquiriesWithScheduledCalls.reduce((sum, i) => {
            const created = new Date(i.created_at);
            const scheduled = new Date(i.scheduled_call_at!);
            const diffHours = (scheduled.getTime() - created.getTime()) / (1000 * 60 * 60);
            return sum + Math.max(0, diffHours);
          }, 0);
          avgTimeToScheduleCall = totalHoursToSchedule / inquiriesWithScheduledCalls.length;
        }

        // 2. Time to send contract (inquiry created -> contract sent_at)
        // Create a map of inquiry_id -> created_at for lookup
        const inquiryCreatedMap = new Map(
          inquiriesResult.data.map(i => [i.id, new Date(i.created_at)])
        );
        
        const contractsWithSentAt = (contractsResult.data || []).filter(
          c => c.sent_at && c.inquiry_id && inquiryCreatedMap.has(c.inquiry_id)
        );
        let avgTimeToSendContract: number | null = null;
        if (contractsWithSentAt.length > 0) {
          const totalHoursToSend = contractsWithSentAt.reduce((sum, c) => {
            const inquiryCreated = inquiryCreatedMap.get(c.inquiry_id!);
            if (!inquiryCreated) return sum;
            const sentAt = new Date(c.sent_at!);
            const diffHours = (sentAt.getTime() - inquiryCreated.getTime()) / (1000 * 60 * 60);
            return sum + Math.max(0, diffHours);
          }, 0);
          avgTimeToSendContract = totalHoursToSend / contractsWithSentAt.length;
        }

        // 3. Time to payment (contract sent -> payment received)
        // Create a map of contract_id -> sent_at for lookup
        const contractSentMap = new Map(
          (contractsResult.data || [])
            .filter(c => c.sent_at)
            .map(c => [c.id, new Date(c.sent_at!)])
        );
        
        const paymentsReceived = (paymentsResult.data || []).filter(
          p => p.status === 'received' && p.received_date && p.contract_id && contractSentMap.has(p.contract_id)
        );
        let avgTimeToPayment: number | null = null;
        if (paymentsReceived.length > 0) {
          const totalDaysToPayment = paymentsReceived.reduce((sum, p) => {
            const contractSent = contractSentMap.get(p.contract_id!);
            if (!contractSent) return sum;
            const receivedDate = new Date(p.received_date!);
            const diffDays = (receivedDate.getTime() - contractSent.getTime()) / (1000 * 60 * 60 * 24);
            return sum + Math.max(0, diffDays);
          }, 0);
          avgTimeToPayment = totalDaysToPayment / paymentsReceived.length;
        }

        setSalesTiming({
          avgTimeToScheduleCall,
          avgTimeToSendContract,
          avgTimeToPayment,
          callsScheduled: inquiriesWithScheduledCalls.length,
          contractsSent: contractsWithSentAt.length,
          paymentsReceived: paymentsReceived.length,
        });

        // Process contracts and financials
        let contractedValue = 0;
        let signedValue = 0;
        
        if (contractsResult.data) {
          contractedValue = contractsResult.data.reduce((sum, c) => sum + (c.total_amount || 0), 0);
          signedValue = contractsResult.data
            .filter(c => c.status === 'signed')
            .reduce((sum, c) => sum + (c.total_amount || 0), 0);
        }

        // Process payments
        let collectionsReceived = 0;
        let collectionsPending = 0;
        
        if (paymentsResult.data) {
          collectionsReceived = paymentsResult.data
            .filter(p => p.status === 'received')
            .reduce((sum, p) => sum + (p.amount || 0), 0);
          collectionsPending = paymentsResult.data
            .filter(p => p.status === 'pending')
            .reduce((sum, p) => sum + (p.amount || 0), 0);
        }

        // Process expenses
        let totalExpenses = 0;
        if (expensesResult.data) {
          totalExpenses = expensesResult.data.reduce((sum, e) => sum + (e.amount || 0), 0);
        }

        // Calculate profit
        const grossProfit = collectionsReceived - totalExpenses;
        const profitMargin = collectionsReceived > 0 ? (grossProfit / collectionsReceived * 100) : 0;

        setFinancials({
          pipelineValue,
          contractedValue,
          signedValue,
          collectionsReceived,
          collectionsPending,
          totalExpenses,
          grossProfit,
          profitMargin,
        });
      }

      // Process signups
      setTotalSignups(profilesResult.count || 0);
      setPreviousSignups(previousProfilesResult.count || 0);

      // Process NPS
      if (feedbackResult.data && feedbackResult.data.length > 0) {
        const scores = feedbackResult.data.filter(f => f.nps_score !== null);
        const promoters = scores.filter(f => f.nps_score! >= 9).length;
        const passives = scores.filter(f => f.nps_score! >= 7 && f.nps_score! <= 8).length;
        const detractors = scores.filter(f => f.nps_score! <= 6).length;
        const total = scores.length;
        const npsScore = total > 0 ? Math.round(((promoters - detractors) / total) * 100) : 0;
        
        setNpsData({
          score: npsScore,
          promoters,
          passives,
          detractors,
          totalResponses: total,
        });
      }

      // Process errors
      setErrorCount(errorsResult.count || 0);

      // Process tasks
      if (tasksResult.data) setTasks(tasksResult.data);

    } catch (error: any) {
      console.error('Failed to load executive data:', error);
      toast({
        title: 'Error Loading Data',
        description: error.message || 'Failed to load analytics',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [timeRange, toast]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  const createTask = async () => {
    if (!newTask.title.trim()) {
      toast({ title: 'Title required', variant: 'destructive' });
      return;
    }

    try {
      const { error } = await supabase
        .from('employee_tasks')
        .insert({
          title: newTask.title,
          description: newTask.description || null,
          priority: newTask.priority,
          due_date: newTask.due_date || null,
        });

      if (error) throw error;

      toast({ title: 'Task created' });
      setNewTask({ title: '', description: '', priority: 'medium', due_date: '' });
      setNewTaskOpen(false);
      loadAllData();
    } catch (error: any) {
      toast({ title: 'Failed to create task', description: error.message, variant: 'destructive' });
    }
  };

  const updateTaskStatus = async (taskId: string, status: string) => {
    try {
      const updateData: any = { status };
      if (status === 'completed') {
        updateData.completed_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('employee_tasks')
        .update(updateData)
        .eq('id', taskId);

      if (error) throw error;
      loadAllData();
    } catch (error: any) {
      toast({ title: 'Failed to update task', description: error.message, variant: 'destructive' });
    }
  };

  const deleteTask = async (taskId: string) => {
    try {
      const { error } = await supabase
        .from('employee_tasks')
        .delete()
        .eq('id', taskId);

      if (error) throw error;
      toast({ title: 'Task deleted' });
      loadAllData();
    } catch (error: any) {
      toast({ title: 'Failed to delete task', description: error.message, variant: 'destructive' });
    }
  };

  // Calculate all metrics
  const metrics = useMemo(() => {
    if (!sessions.length) return null;

    const uniqueVisitors = new Set(sessions.map(s => s.session_id)).size;
    const totalTimeSpent = sessions.reduce((sum, s) => sum + (s.time_spent_seconds || 0), 0);
    const avgTimeSpent = Math.round(totalTimeSpent / sessions.length);
    const totalInteractions = sessions.reduce((sum, s) => sum + ((s.interactions as any[])?.length || 0), 0);
    const bounceRate = sessions.filter(s => (s.time_spent_seconds || 0) < 10).length / sessions.length * 100;

    // Button click analytics
    const buttonClicks: Record<string, number> = {};
    sessions.forEach(s => {
      if (Array.isArray(s.interactions)) {
        s.interactions.forEach((interaction: any) => {
          if (interaction.type === 'click') {
            const target = interaction.target || interaction.details?.element || 'unknown';
            buttonClicks[target] = (buttonClicks[target] || 0) + 1;
          }
        });
      }
    });

    const topButtons = Object.entries(buttonClicks)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 15)
      .map(([name, count], index) => ({ 
        name: formatButtonName(name), 
        count, 
        fullName: name,
        rank: index + 1,
        heat: Math.min(100, Math.round((count / Math.max(...Object.values(buttonClicks))) * 100)),
      }));

    // Device breakdown
    const devices: Record<string, number> = {};
    sessions.forEach(s => {
      const device = s.device_type || 'unknown';
      devices[device] = (devices[device] || 0) + 1;
    });
    const deviceData = Object.entries(devices).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value,
      percentage: Math.round((value / sessions.length) * 100),
    }));

    // Browser breakdown
    const browsers: Record<string, number> = {};
    sessions.forEach(s => {
      const browser = s.browser || 'unknown';
      browsers[browser] = (browsers[browser] || 0) + 1;
    });
    const browserData = Object.entries(browsers)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([name, value]) => ({ name, value }));

    // Page performance
    const pageMap: Record<string, { visits: number; totalTime: number; interactions: number }> = {};
    sessions.forEach(s => {
      const existing = pageMap[s.page_route] || { visits: 0, totalTime: 0, interactions: 0 };
      pageMap[s.page_route] = {
        visits: existing.visits + 1,
        totalTime: existing.totalTime + (s.time_spent_seconds || 0),
        interactions: existing.interactions + ((s.interactions as any[])?.length || 0),
      };
    });
    const pagePerformance = Object.entries(pageMap)
      .map(([route, data]) => ({
        route: formatRouteName(route),
        fullRoute: route,
        visits: data.visits,
        avgTime: Math.round(data.totalTime / data.visits),
        avgInteractions: Math.round(data.interactions / data.visits),
        engagementScore: Math.round((data.interactions / data.visits) * (data.totalTime / data.visits / 10)),
      }))
      .sort((a, b) => b.visits - a.visits);

    // Location data
    const locationMap: Record<string, LocationData> = {};
    sessions.forEach(s => {
      if (s.latitude && s.longitude && s.city && s.country_name) {
        const key = `${s.city}-${s.country_name}`;
        const existing = locationMap[key];
        if (existing) {
          existing.count++;
        } else {
          locationMap[key] = {
            latitude: s.latitude,
            longitude: s.longitude,
            city: s.city,
            country: s.country_name,
            count: 1,
          };
        }
      }
    });
    const locationData = Object.values(locationMap).sort((a, b) => b.count - a.count);

    // Country breakdown
    const countries: Record<string, number> = {};
    sessions.forEach(s => {
      if (s.country_name) {
        countries[s.country_name] = (countries[s.country_name] || 0) + 1;
      }
    });
    const countryData = Object.entries(countries)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 8)
      .map(([name, count]) => ({ name, count, percentage: Math.round((count / sessions.length) * 100) }));

    // Daily trend
    const startDate = subDays(new Date(), parseInt(timeRange));
    const days = eachDayOfInterval({ start: startDate, end: new Date() });
    const dailyData = days.map(day => {
      const dayStart = startOfDay(day);
      const dayEnd = endOfDay(day);
      const daySessions = sessions.filter(s => {
        const sessionDate = new Date(s.created_at);
        return sessionDate >= dayStart && sessionDate <= dayEnd;
      });
      return {
        date: format(day, 'MMM dd'),
        visitors: new Set(daySessions.map(s => s.session_id)).size,
        sessions: daySessions.length,
        interactions: daySessions.reduce((sum, s) => sum + ((s.interactions as any[])?.length || 0), 0),
      };
    });

    // Hourly activity
    const hourlyActivity: Record<number, number> = {};
    sessions.forEach(s => {
      const hour = new Date(s.created_at).getHours();
      hourlyActivity[hour] = (hourlyActivity[hour] || 0) + 1;
    });
    const hourlyData = Array.from({ length: 24 }, (_, i) => ({
      hour: i,
      label: `${i.toString().padStart(2, '0')}:00`,
      sessions: hourlyActivity[i] || 0,
    }));
    const peakHour = hourlyData.reduce((max, curr) => curr.sessions > max.sessions ? curr : max, hourlyData[0]);

    return {
      uniqueVisitors,
      totalSessions: sessions.length,
      avgTimeSpent,
      totalInteractions,
      bounceRate: Math.round(bounceRate),
      topButtons,
      deviceData,
      browserData,
      pagePerformance,
      locationData,
      countryData,
      dailyData,
      hourlyData,
      peakHour,
    };
  }, [sessions, timeRange]);

  // Task stats
  const taskStats = useMemo(() => {
    const pending = tasks.filter(t => t.status === 'pending').length;
    const inProgress = tasks.filter(t => t.status === 'in_progress').length;
    const completed = tasks.filter(t => t.status === 'completed').length;
    const urgent = tasks.filter(t => t.priority === 'urgent' && t.status !== 'completed').length;
    return { pending, inProgress, completed, urgent, total: tasks.length };
  }, [tasks]);

  const getDeviceIcon = (device: string) => {
    switch (device.toLowerCase()) {
      case 'mobile': return <Smartphone className="h-4 w-4" />;
      case 'tablet': return <Tablet className="h-4 w-4" />;
      default: return <Monitor className="h-4 w-4" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500/20 text-red-700 border-red-500/30';
      case 'high': return 'bg-orange-500/20 text-orange-700 border-orange-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-700 border-yellow-500/30';
      default: return 'bg-green-500/20 text-green-700 border-green-500/30';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'in_progress': return <Timer className="h-4 w-4 text-blue-500" />;
      default: return <Circle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getNPSColor = (score: number) => {
    if (score >= 50) return 'text-green-500';
    if (score >= 0) return 'text-yellow-500';
    return 'text-red-500';
  };

  const signupTrend = previousSignups > 0 
    ? Math.round(((totalSignups - previousSignups) / previousSignups) * 100)
    : totalSignups > 0 ? 100 : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center space-y-4">
          <div className="relative">
            <BarChart3 className="w-16 h-16 text-primary mx-auto animate-pulse" />
            <div className="absolute inset-0 bg-primary/20 rounded-full animate-ping" />
          </div>
          <p className="text-muted-foreground text-lg">Loading executive insights...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            Executive Dashboard
          </h2>
          <p className="text-muted-foreground mt-1">
            Business intelligence, financials & performance metrics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={loadAllData} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="60">Last 60 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Financial KPIs - Primary Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border-emerald-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Pipeline Value</p>
                <p className="text-2xl font-bold text-emerald-500">{formatCurrency(financials.pipelineValue)}</p>
                <p className="text-xs text-muted-foreground mt-1">{pendingInquiries} pending inquiries</p>
              </div>
              <div className="p-3 bg-emerald-500/20 rounded-full">
                <ShoppingCart className="h-5 w-5 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500/20 to-blue-500/5 border-blue-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Contracted</p>
                <p className="text-2xl font-bold text-blue-500">{formatCurrency(financials.contractedValue)}</p>
                <p className="text-xs text-muted-foreground mt-1">Signed: {formatCurrency(financials.signedValue)}</p>
              </div>
              <div className="p-3 bg-blue-500/20 rounded-full">
                <FileText className="h-5 w-5 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500/20 to-green-500/5 border-green-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">In Bank</p>
                <p className="text-2xl font-bold text-green-500">{formatCurrency(financials.collectionsReceived)}</p>
                <p className="text-xs text-muted-foreground mt-1">Pending: {formatCurrency(financials.collectionsPending)}</p>
              </div>
              <div className="p-3 bg-green-500/20 rounded-full">
                <BanknoteIcon className="h-5 w-5 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500/20 to-amber-500/5 border-amber-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">COGS / Expenses</p>
                <p className="text-2xl font-bold text-amber-500">{formatCurrency(financials.totalExpenses)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Margin: {financials.profitMargin.toFixed(1)}%
                </p>
              </div>
              <div className="p-3 bg-amber-500/20 rounded-full">
                <Receipt className="h-5 w-5 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Secondary KPIs Row */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${npsData.score >= 50 ? 'bg-green-500/20' : npsData.score >= 0 ? 'bg-yellow-500/20' : 'bg-red-500/20'}`}>
                <Star className={`h-4 w-4 ${getNPSColor(npsData.score)}`} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">NPS Score</p>
                <p className={`text-xl font-bold ${getNPSColor(npsData.score)}`}>
                  {npsData.totalResponses > 0 ? npsData.score : '—'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-purple-500/20">
                <Target className="h-4 w-4 text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Conversion</p>
                <p className="text-xl font-bold text-purple-500">{conversionRate}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-red-500/20">
                <UserX className="h-4 w-4 text-red-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Churn Rate</p>
                <p className="text-xl font-bold text-red-500">{churnRate}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-500/20">
                <Users className="h-4 w-4 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Visitors</p>
                <p className="text-xl font-bold">{metrics?.uniqueVisitors.toLocaleString() || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${signupTrend >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                <UserPlus className={`h-4 w-4 ${signupTrend >= 0 ? 'text-green-500' : 'text-red-500'}`} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Signups</p>
                <div className="flex items-center gap-1">
                  <p className="text-xl font-bold">{totalSignups}</p>
                  {signupTrend !== 0 && (
                    <span className={`text-xs flex items-center ${signupTrend >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {signupTrend >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                      {Math.abs(signupTrend)}%
                    </span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${errorCount > 10 ? 'bg-red-500/20' : errorCount > 0 ? 'bg-yellow-500/20' : 'bg-green-500/20'}`}>
                <AlertTriangle className={`h-4 w-4 ${errorCount > 10 ? 'text-red-500' : errorCount > 0 ? 'text-yellow-500' : 'text-green-500'}`} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Errors</p>
                <p className={`text-xl font-bold ${errorCount > 10 ? 'text-red-500' : errorCount > 0 ? 'text-yellow-500' : 'text-green-500'}`}>
                  {errorCount}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sales Timing Metrics Row */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Timer className="h-4 w-4 text-primary" />
            Sales Pipeline Timing
          </CardTitle>
          <CardDescription>Average time between key milestones</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Time to Schedule Call */}
            <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/30 border">
              <div className="p-3 rounded-full bg-sky-500/20">
                <Phone className="h-5 w-5 text-sky-500" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Inquiry → Call Scheduled</p>
                <p className="text-2xl font-bold text-sky-500">
                  {formatTimingHours(salesTiming.avgTimeToScheduleCall)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {salesTiming.callsScheduled} calls scheduled
                </p>
              </div>
            </div>

            {/* Time to Send Contract */}
            <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/30 border">
              <div className="p-3 rounded-full bg-violet-500/20">
                <Send className="h-5 w-5 text-violet-500" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Inquiry → Contract Sent</p>
                <p className="text-2xl font-bold text-violet-500">
                  {formatTimingHours(salesTiming.avgTimeToSendContract)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {salesTiming.contractsSent} contracts sent
                </p>
              </div>
            </div>

            {/* Time to Payment */}
            <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/30 border">
              <div className="p-3 rounded-full bg-emerald-500/20">
                <CreditCard className="h-5 w-5 text-emerald-500" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Contract Sent → Payment</p>
                <p className="text-2xl font-bold text-emerald-500">
                  {formatTimingDays(salesTiming.avgTimeToPayment)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {salesTiming.paymentsReceived} payments received
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="overview">
            <PieChartIcon className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="traffic">
            <Activity className="h-4 w-4 mr-2" />
            Traffic
          </TabsTrigger>
          <TabsTrigger value="heatmap">
            <MousePointerClick className="h-4 w-4 mr-2" />
            Heatmap
          </TabsTrigger>
          <TabsTrigger value="tasks">
            <ListTodo className="h-4 w-4 mr-2" />
            Tasks
            {taskStats.urgent > 0 && (
              <Badge variant="destructive" className="ml-2 text-xs">{taskStats.urgent}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Pipeline Funnel */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-primary" />
                  Revenue Funnel
                </CardTitle>
                <CardDescription>From inquiry to cash collected</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="relative">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">Pipeline</span>
                      <span className="text-sm font-bold text-emerald-500">{formatCurrency(financials.pipelineValue)}</span>
                    </div>
                    <Progress value={100} className="h-3 bg-emerald-500/20" />
                  </div>
                  <div className="relative">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">Contracted</span>
                      <span className="text-sm font-bold text-blue-500">{formatCurrency(financials.contractedValue)}</span>
                    </div>
                    <Progress 
                      value={financials.pipelineValue > 0 ? (financials.contractedValue / financials.pipelineValue * 100) : 0} 
                      className="h-3 bg-blue-500/20" 
                    />
                  </div>
                  <div className="relative">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">Signed</span>
                      <span className="text-sm font-bold text-indigo-500">{formatCurrency(financials.signedValue)}</span>
                    </div>
                    <Progress 
                      value={financials.pipelineValue > 0 ? (financials.signedValue / financials.pipelineValue * 100) : 0} 
                      className="h-3 bg-indigo-500/20" 
                    />
                  </div>
                  <div className="relative">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">Collected (In Bank)</span>
                      <span className="text-sm font-bold text-green-500">{formatCurrency(financials.collectionsReceived)}</span>
                    </div>
                    <Progress 
                      value={financials.pipelineValue > 0 ? (financials.collectionsReceived / financials.pipelineValue * 100) : 0} 
                      className="h-3 bg-green-500/20" 
                    />
                  </div>
                  <Separator className="my-4" />
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="p-3 bg-green-500/10 rounded-lg">
                      <p className="text-xs text-muted-foreground">Gross Profit</p>
                      <p className="text-lg font-bold text-green-500">{formatCurrency(financials.grossProfit)}</p>
                    </div>
                    <div className="p-3 bg-amber-500/10 rounded-lg">
                      <p className="text-xs text-muted-foreground">Pending Collections</p>
                      <p className="text-lg font-bold text-amber-500">{formatCurrency(financials.collectionsPending)}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* NPS Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-primary" />
                  Net Promoter Score
                </CardTitle>
                <CardDescription>Customer satisfaction breakdown</CardDescription>
              </CardHeader>
              <CardContent>
                {npsData.totalResponses > 0 ? (
                  <div className="space-y-6">
                    <div className="text-center">
                      <div className={`text-5xl font-bold ${getNPSColor(npsData.score)}`}>
                        {npsData.score}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        Based on {npsData.totalResponses} responses
                      </p>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <ThumbsUp className="h-5 w-5 text-green-500" />
                        <div className="flex-1">
                          <div className="flex justify-between text-sm mb-1">
                            <span>Promoters (9-10)</span>
                            <span className="font-medium">{npsData.promoters}</span>
                          </div>
                          <Progress 
                            value={npsData.totalResponses > 0 ? (npsData.promoters / npsData.totalResponses * 100) : 0} 
                            className="h-2 bg-green-500/20" 
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Meh className="h-5 w-5 text-yellow-500" />
                        <div className="flex-1">
                          <div className="flex justify-between text-sm mb-1">
                            <span>Passives (7-8)</span>
                            <span className="font-medium">{npsData.passives}</span>
                          </div>
                          <Progress 
                            value={npsData.totalResponses > 0 ? (npsData.passives / npsData.totalResponses * 100) : 0} 
                            className="h-2 bg-yellow-500/20" 
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <ThumbsDown className="h-5 w-5 text-red-500" />
                        <div className="flex-1">
                          <div className="flex justify-between text-sm mb-1">
                            <span>Detractors (0-6)</span>
                            <span className="font-medium">{npsData.detractors}</span>
                          </div>
                          <Progress 
                            value={npsData.totalResponses > 0 ? (npsData.detractors / npsData.totalResponses * 100) : 0} 
                            className="h-2 bg-red-500/20" 
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Star className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No NPS data collected yet</p>
                    <p className="text-xs mt-1">Send surveys after events to collect feedback</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Visitor Trend Chart */}
          {metrics?.dailyData && metrics.dailyData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  Daily Performance
                </CardTitle>
                <CardDescription>Visitors and engagement over time</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={metrics.dailyData}>
                      <defs>
                        <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(160, 84%, 39%)" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="hsl(160, 84%, 39%)" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--popover))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="visitors" 
                        stroke="hsl(160, 84%, 39%)" 
                        fillOpacity={1} 
                        fill="url(#colorVisitors)"
                        name="Visitors"
                      />
                      <Bar dataKey="interactions" fill="hsl(200, 80%, 50%)" name="Interactions" barSize={20} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Devices & Geography */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {metrics?.deviceData && metrics.deviceData.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Smartphone className="h-5 w-5 text-primary" />
                    Devices
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-6">
                    <div className="h-[140px] w-[140px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={metrics.deviceData}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={65}
                            paddingAngle={3}
                            dataKey="value"
                          >
                            {metrics.deviceData.map((_, index) => (
                              <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-2 flex-1">
                      {metrics.deviceData.map((device, idx) => (
                        <div key={device.name} className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                          />
                          <div className="flex items-center gap-2 flex-1">
                            {getDeviceIcon(device.name)}
                            <span className="text-sm">{device.name}</span>
                          </div>
                          <span className="text-sm font-medium">{device.percentage}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {metrics?.locationData && metrics.locationData.length > 0 && (
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5 text-primary" />
                    Visitor Geography
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <GeographyMap locations={metrics.locationData} />
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Traffic Tab */}
        <TabsContent value="traffic" className="space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="text-center">
                  <Activity className="h-8 w-8 mx-auto text-primary mb-2" />
                  <p className="text-2xl font-bold">{metrics?.totalSessions.toLocaleString() || 0}</p>
                  <p className="text-xs text-muted-foreground">Total Sessions</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-center">
                  <Clock className="h-8 w-8 mx-auto text-blue-500 mb-2" />
                  <p className="text-2xl font-bold">{formatDuration(metrics?.avgTimeSpent || 0)}</p>
                  <p className="text-xs text-muted-foreground">Avg. Time on Site</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-center">
                  <MousePointerClick className="h-8 w-8 mx-auto text-amber-500 mb-2" />
                  <p className="text-2xl font-bold">{metrics?.totalInteractions.toLocaleString() || 0}</p>
                  <p className="text-xs text-muted-foreground">Total Interactions</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-center">
                  <XCircle className="h-8 w-8 mx-auto text-red-500 mb-2" />
                  <p className="text-2xl font-bold">{metrics?.bounceRate || 0}%</p>
                  <p className="text-xs text-muted-foreground">Bounce Rate</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Page Performance Table */}
          <Card>
            <CardHeader>
              <CardTitle>Page Performance</CardTitle>
              <CardDescription>Engagement metrics by page</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {metrics?.pagePerformance.slice(0, 8).map((page, idx) => (
                  <div key={page.fullRoute} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-bold text-muted-foreground w-6">{idx + 1}</span>
                      <div>
                        <p className="font-medium">{page.route}</p>
                        <p className="text-xs text-muted-foreground">{page.fullRoute}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <div className="text-center">
                        <p className="font-medium">{page.visits}</p>
                        <p className="text-xs text-muted-foreground">Visits</p>
                      </div>
                      <div className="text-center">
                        <p className="font-medium">{formatDuration(page.avgTime)}</p>
                        <p className="text-xs text-muted-foreground">Avg Time</p>
                      </div>
                      <div className="text-center">
                        <p className="font-medium">{page.avgInteractions}</p>
                        <p className="text-xs text-muted-foreground">Actions</p>
                      </div>
                      <div className="text-center min-w-[60px]">
                        <Badge variant={page.engagementScore > 50 ? 'default' : 'secondary'}>
                          {page.engagementScore}
                        </Badge>
                        <p className="text-xs text-muted-foreground">Score</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Hourly Activity */}
          {metrics?.hourlyData && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  Activity by Hour
                </CardTitle>
                <CardDescription>
                  Peak hour: {metrics.peakHour.label} with {metrics.peakHour.sessions} sessions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={metrics.hourlyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={10} interval={2} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--popover))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <Bar dataKey="sessions" fill="hsl(200, 80%, 50%)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Heatmap Tab */}
        <TabsContent value="heatmap" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Hand className="h-5 w-5 text-primary" />
                Click Heatmap
              </CardTitle>
              <CardDescription>Most clicked elements on your site</CardDescription>
            </CardHeader>
            <CardContent>
              {metrics?.topButtons && metrics.topButtons.length > 0 ? (
                <div className="space-y-2">
                  {metrics.topButtons.map((button) => (
                    <div 
                      key={button.fullName} 
                      className="flex items-center gap-4 p-3 rounded-lg transition-all hover:scale-[1.01]"
                      style={{
                        background: `linear-gradient(90deg, hsl(0, ${Math.min(100, button.heat)}%, 50%, ${button.heat / 100 * 0.3}) ${button.heat}%, transparent ${button.heat}%)`
                      }}
                    >
                      <span className="text-lg font-bold text-muted-foreground w-8">#{button.rank}</span>
                      <div className="flex-1">
                        <p className="font-medium">{button.name}</p>
                        <p className="text-xs text-muted-foreground truncate max-w-xs">{button.fullName}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: `hsl(${Math.max(0, 120 - button.heat)}, 70%, 50%)` }}
                        />
                        <span className="font-bold tabular-nums">{button.count}</span>
                        <span className="text-xs text-muted-foreground">clicks</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <MousePointerClick className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No click data available yet</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Browser Stats */}
          {metrics?.browserData && metrics.browserData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Browser Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={metrics.browserData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <YAxis dataKey="name" type="category" stroke="hsl(var(--muted-foreground))" fontSize={12} width={80} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--popover))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <Bar dataKey="value" fill="hsl(280, 70%, 55%)" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks" className="space-y-6">
          {/* Task Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-muted">
                    <Layers className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total</p>
                    <p className="text-xl font-bold">{taskStats.total}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-yellow-500/20">
                    <Circle className="h-4 w-4 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Pending</p>
                    <p className="text-xl font-bold text-yellow-500">{taskStats.pending}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-blue-500/20">
                    <Timer className="h-4 w-4 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">In Progress</p>
                    <p className="text-xl font-bold text-blue-500">{taskStats.inProgress}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-green-500/20">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Completed</p>
                    <p className="text-xl font-bold text-green-500">{taskStats.completed}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-red-500/20">
                    <AlertCircle className="h-4 w-4 text-red-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Urgent</p>
                    <p className="text-xl font-bold text-red-500">{taskStats.urgent}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Task List */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Task Management</CardTitle>
                <CardDescription>Track team tasks and progress</CardDescription>
              </div>
              <Dialog open={newTaskOpen} onOpenChange={setNewTaskOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="gap-2">
                    <Plus className="h-4 w-4" />
                    New Task
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Task</DialogTitle>
                    <DialogDescription>Add a task for your team</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Title</Label>
                      <Input 
                        id="title"
                        value={newTask.title}
                        onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Task title..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea 
                        id="description"
                        value={newTask.description}
                        onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Optional description..."
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Priority</Label>
                        <Select 
                          value={newTask.priority} 
                          onValueChange={(val) => setNewTask(prev => ({ ...prev, priority: val }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="urgent">Urgent</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="due_date">Due Date</Label>
                        <Input 
                          id="due_date"
                          type="date"
                          value={newTask.due_date}
                          onChange={(e) => setNewTask(prev => ({ ...prev, due_date: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setNewTaskOpen(false)}>Cancel</Button>
                    <Button onClick={createTask}>Create Task</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-2">
                  {tasks.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <ListTodo className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p>No tasks yet</p>
                      <p className="text-xs mt-1">Create your first task to get started</p>
                    </div>
                  ) : (
                    tasks.map((task) => (
                      <div 
                        key={task.id} 
                        className={`flex items-center gap-4 p-4 rounded-lg border ${
                          task.status === 'completed' ? 'bg-green-500/5 border-green-500/20' : 'bg-muted/50'
                        }`}
                      >
                        {getStatusIcon(task.status)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                              {task.title}
                            </p>
                            <Badge className={getPriorityColor(task.priority)} variant="outline">
                              {task.priority}
                            </Badge>
                          </div>
                          {task.description && (
                            <p className="text-sm text-muted-foreground truncate mt-1">{task.description}</p>
                          )}
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            {task.due_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {format(new Date(task.due_date), 'MMM d, yyyy')}
                              </span>
                            )}
                            <span>Created {format(new Date(task.created_at), 'MMM d')}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Select 
                            value={task.status} 
                            onValueChange={(val) => updateTaskStatus(task.id, val)}
                          >
                            <SelectTrigger className="w-[130px] h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => deleteTask(task.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
